import { useState } from 'react';
import { API_BASE } from '../../../config';
import { deserializeNode } from '../../../utils/serializeNode';
import type { StrategyNode } from '../../../types/BaseNode';

interface AiAssistantProps {
  onSuccess: (node: StrategyNode) => void;
}

export function AiAssistant({ onSuccess }: AiAssistantProps) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/strategy/ai/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });

      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.error || `Server error: ${resp.status}`);
      }

      const data = await resp.json();
      if (data.success && data.strategy) {
        const node = deserializeNode(data.strategy);
        onSuccess(node.clone());
        setPrompt('');
      } else {
        alert("AI Error: " + (data.error || "Failed to generate strategy"));
      }
    } catch (err: any) {
      console.error("AI Generation failed:", err);
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
      width: '100%',
      maxWidth: '1200px', // Wider
      margin: '0 auto',
      background: 'var(--bg-card)',
      padding: '0.75rem 1.5rem',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--surface-glass-border)',
      boxShadow: 'var(--shadow-overlay-lg)',
      transition: 'all 0.2s',
      animation: 'fadeIn 0.5s ease-out'
    }}>
      <div style={{ fontSize: '1.2rem' }}>✨</div>
      <input
        type="text"
        placeholder="Describe a complex trading strategy... (e.g. 'If DA > WD then long, else short')"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        disabled={loading}
        onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
        style={{
          flexGrow: 1,
          padding: '0.75rem 1rem',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--surface-glass-border)',
          fontSize: '1rem',
          outline: 'none',
          backgroundColor: 'var(--surface-dark-soft)',
          color: 'var(--text-primary)',
          fontFamily: 'inherit'
        }}
      />
      <button
        onClick={handleGenerate}
        disabled={loading || !prompt.trim()}
        style={{
          backgroundColor: loading ? 'var(--text-muted)' : 'var(--accent-primary)',
          color: 'var(--text-primary)',
          padding: '0.75rem 2rem',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          fontSize: '1rem',
          fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          whiteSpace: 'nowrap',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          boxShadow: loading ? 'none' : '0 4px 12px var(--accent-glow)'
        }}
      >
        {loading ? (
          <>⏳ <span>Thinking...</span></>
        ) : (
          <span>Generate Strategy</span>
        )}
      </button>
    </div>
  );
}


